import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";
import { Profile } from "./auth-types";
import {
  supabaseSelectSingle,
  supabaseSelectMaybeSingle,
} from "../lib/supabaseApi";
import { getSupportProduct } from "../services/supportProduct";
import { logger } from "../lib/logger";
import { createOncePerSignIn } from "../lib/oncePerSignIn";
import { getAttribution, clearAttribution } from "../lib/utmTracking";
import i18n, { setLocale, type AppLocale } from "../i18n";
import { peekAuthReturnTo, stripKnownBasename } from "../lib/authReturnTo";

// Exporter Profile pour qu'il puisse être utilisé dans d'autres modules
export type { Profile };

/** Colonnes d'abonnement superposables (product_subscriptions) sur un Profile. */
type ProductSubscriptionRow = {
  plan_type: string;
  lemonsqueezy_subscription_id: string | null;
  lemonsqueezy_status: string | null;
  lemonsqueezy_current_period_start: string | null;
  lemonsqueezy_current_period_end: string | null;
  lemonsqueezy_cancel_at_period_end: boolean | null;
  lemonsqueezy_card_brand: string | null;
  lemonsqueezy_card_last4: string | null;
  // Essai Solo (migration 20260921130000). Overlayés comme le reste : sans eux,
  // `isTrialActive` lirait toujours undefined et l'essai serait invisible.
  trial_ends_at: string | null;
  trial_used_at: string | null;
  trial_campaign: string | null;
  // Réponse à la PROPOSITION (migration 20260921141000). Sans overlay,
  // `trialOfferPending` lirait undefined et le bandeau reviendrait à chaque
  // chargement chez quelqu'un qui a répondu « non merci » — le contraire
  // exact de ce qu'il a demandé.
  trial_offer_declined_at: string | null;
  trial_offer_snoozed_until: string | null;
  trial_offer_campaign: string | null;
};

/**
 * Superpose l'abonnement du PRODUIT COURANT (getSupportProduct) sur le profil.
 * - Ligne trouvée  → plan_type + lemonsqueezy_* du produit courant.
 * - Aucune ligne   → gratuit sur ce produit (peu importe le plan_type global).
 * - Erreur lecture → fail-closed sur 'free' : on ne fuite JAMAIS un plan payant
 *   d'un autre produit (un abonné DooQuest ne doit pas voir DooDates payant).
 */
async function overlayCurrentProductSubscription(
  profile: Profile,
  userId: string,
  timeoutMs: number,
): Promise<void> {
  const target = profile as Profile & Partial<ProductSubscriptionRow>;
  const clearPaid = () => {
    target.plan_type = "free";
    target.lemonsqueezy_subscription_id = null;
    target.lemonsqueezy_status = null;
    target.lemonsqueezy_current_period_start = null;
    target.lemonsqueezy_current_period_end = null;
    target.lemonsqueezy_cancel_at_period_end = false;
    target.lemonsqueezy_card_brand = null;
    target.lemonsqueezy_card_last4 = null;
    // Fail-closed sur l'essai aussi : en cas d'échec de lecture, mieux vaut
    // masquer un essai réel que d'en afficher un qui n'existe pas.
    target.trial_ends_at = null;
    target.trial_used_at = null;
    target.trial_campaign = null;
    // Fail-closed ici veut dire l'INVERSE de l'essai : en cas d'échec de
    // lecture on efface la réponse, donc le bandeau réapparaît. Masquer une
    // proposition est réversible d'un clic ; afficher un essai qui n'existe
    // pas est une fausse promesse.
    target.trial_offer_declined_at = null;
    target.trial_offer_snoozed_until = null;
    target.trial_offer_campaign = null;
  };
  try {
    const product = getSupportProduct();

    // Retry logic : le token peut ne pas être disponible immédiatement après auth
    // On retry 2x avec un délai court si la première tentative échoue
    let sub: ProductSubscriptionRow | null = null;
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= 2; attempt++) {
      try {
        sub = await supabaseSelectMaybeSingle<ProductSubscriptionRow>(
          "product_subscriptions",
          { user_id: `eq.${userId}`, product: `eq.${product}`, select: "*" },
          { timeout: Math.min(timeoutMs / 2, 5000), requireAuth: true },
        );
        break; // Succès
      } catch (err) {
        lastError = err instanceof Error ? err : new Error(String(err));
        if (attempt === 1) {
          logger.debug(
            `product_subscriptions attempt ${attempt} failed, retrying…`,
            "auth",
            { error: lastError.message },
          );
          await new Promise((r) => setTimeout(r, 200)); // Backoff court
        }
      }
    }

    if (sub) {
      target.plan_type = sub.plan_type;
      target.lemonsqueezy_subscription_id = sub.lemonsqueezy_subscription_id;
      target.lemonsqueezy_status = sub.lemonsqueezy_status;
      target.lemonsqueezy_current_period_start =
        sub.lemonsqueezy_current_period_start;
      target.lemonsqueezy_current_period_end =
        sub.lemonsqueezy_current_period_end;
      target.lemonsqueezy_cancel_at_period_end =
        sub.lemonsqueezy_cancel_at_period_end ?? false;
      target.lemonsqueezy_card_brand = sub.lemonsqueezy_card_brand;
      target.lemonsqueezy_card_last4 = sub.lemonsqueezy_card_last4;
      target.trial_ends_at = sub.trial_ends_at ?? null;
      target.trial_used_at = sub.trial_used_at ?? null;
      target.trial_campaign = sub.trial_campaign ?? null;
      target.trial_offer_declined_at = sub.trial_offer_declined_at ?? null;
      target.trial_offer_snoozed_until = sub.trial_offer_snoozed_until ?? null;
      target.trial_offer_campaign = sub.trial_offer_campaign ?? null;
      logger.debug("product_subscriptions overlay successful", "auth", {
        userId,
        product,
        plan_type: sub.plan_type,
      });
    } else if (lastError) {
      throw lastError; // Re-throw après retries
    } else {
      clearPaid();
      logger.debug(
        "No product_subscriptions row found, defaulting to free",
        "auth",
        { userId, product },
      );
    }
  } catch (subErr) {
    logger.error(
      "product_subscriptions overlay failed after retries; defaulting current-product plan to free (fail-closed)",
      "auth",
      {
        userId,
        err: subErr instanceof Error ? subErr.message : String(subErr),
      },
    );
    clearPaid();
  }
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  error: string | null;
  signIn: (
    email: string,
    password: string,
  ) => Promise<{ error?: string; success?: boolean }>;
  signUp: (
    email: string,
    password: string,
    options?: {
      /**
       * Métadonnées de consentement RGPD injectées dans `raw_user_meta_data`.
       * Lues par le trigger SQL `handle_new_user` qui peuple `profiles` et
       * `user_consents` pour constituer la preuve juridique (art. 7 RGPD).
       */
      termsVersion?: string;
      termsAcceptedAt?: string; // ISO 8601
      fullName?: string;
      /** Variante du CTA viral (A|B|C) ayant amené ce répondant à s'inscrire. */
      ctaVariant?: string;
    },
  ) => Promise<{ error?: string; success?: boolean }>;
  signInWithGoogle: () => Promise<{ error?: string; success?: boolean }>;
  signOut: () => Promise<void>;
  updateProfile: (
    updates: Partial<Profile>,
  ) => Promise<{ error?: string; success?: boolean }>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isLocalDevelopment = import.meta.env.MODE === "development";

  // i18n : à la connexion, la préférence de langue persistée en base prime
  // sur la détection localStorage/navigateur. Pour un visiteur anonyme, on ne
  // touche à rien (son choix localStorage reste).
  useEffect(() => {
    const pref = profile?.preferred_language;
    if (pref && pref !== i18n.language?.slice(0, 2)) {
      void setLocale(pref as AppLocale);
    }
  }, [profile?.preferred_language]);

  const fetchProfile = async (userId: string): Promise<Profile | null> => {
    // 🔒 Sécurité : Validation de l'userId
    if (!userId || typeof userId !== "string" || userId.length < 1) {
      logger.warn("Invalid userId provided to fetchProfile", "auth", {
        userId,
      });
      return null;
    }

    // Timeout généreux + retry : le fetch concurrent avec d'autres appels
    // (polls, quotas, RPC admin) peut dépasser 5s même quand l'API répond vite.
    // Cf. logs postgres : statement_timeout possible côté DB + connection pool.
    const MAX_ATTEMPTS = 3;
    const TIMEOUT_MS = 15000;

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      try {
        const data = await supabaseSelectSingle<Profile>(
          "profiles",
          { id: `eq.${userId}`, select: "*" },
          { timeout: TIMEOUT_MS },
        );

        // 🔒 Sécurité : Validation des données reçues
        if (!data || typeof data !== "object") {
          logger.warn("Invalid profile data received", "auth", {
            userId,
            data,
          });
          return null;
        }

        // 🔒 Sécurité : Validation du plan_type
        if (data.plan_type && typeof data.plan_type === "string") {
          const validPlans = ["free", "solo", "dooquest_pro", "dooform_pro"]; // ✅ Doctrine 27/03/2026 + DooQuest Pro (rename 21/06/2026, dooform_pro legacy)
          const normalizedPlan = data.plan_type.toLowerCase().trim();
          if (!validPlans.includes(normalizedPlan)) {
            logger.warn(
              "Invalid plan_type in profile, normalizing to free",
              "auth",
              { userId, originalPlan: data.plan_type },
            );
            data.plan_type = "free";
          }
        }

        // Multi-produit : le plan pertinent est celui du PRODUIT COURANT, pas
        // le `plan_type` global de la ligne profiles. Une même ligne profiles
        // ne porte qu'UN plan_type, mais un compte peut être payant sur DooDates
        // (product='date', 'solo') ET DooQuest (product='form', 'dooquest_pro')
        // séparément — ce sont deux produits distincts côté client. On superpose
        // l'abonnement product_subscriptions du produit courant sur le profil
        // exposé à l'app : tous les consommateurs (quotas, page « Mon abonnement »,
        // garde-fous payant) deviennent automatiquement scoppés par produit,
        // sans changement de leur côté. Cf. migration 20260708100000.
        await overlayCurrentProductSubscription(data, userId, TIMEOUT_MS);

        if (attempt > 1) {
          logger.info(`Profile fetched on attempt ${attempt}`, "auth", {
            userId,
          });
        }
        return data;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : String(err);
        const isLastAttempt = attempt === MAX_ATTEMPTS;

        if (isLastAttempt) {
          logger.error(
            "Could not fetch profile after retries, continuing without it",
            "auth",
            { userId, attempts: attempt, errorMessage },
          );
          return null;
        }

        logger.warn(
          `Profile fetch attempt ${attempt} failed, retrying…`,
          "auth",
          { userId, errorMessage },
        );
        // Backoff exponentiel : 500ms, 1500ms
        await new Promise((r) => setTimeout(r, 500 * attempt ** 2));
      }
    }

    return null;
  };

  const refreshProfile = async () => {
    if (user) {
      const profileData = await fetchProfile(user.id);
      setProfile(profileData);
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
      return { success: true };
    } catch (err: any) {
      setError(err.message);
      return { error: err.message };
    }
  };

  const signUp = async (
    email: string,
    password: string,
    options?: {
      termsVersion?: string;
      termsAcceptedAt?: string;
      fullName?: string;
      ctaVariant?: string;
    },
  ) => {
    try {
      // Les champs de `data` sont mergés dans `auth.users.raw_user_meta_data`
      // et lus par le trigger SQL `handle_new_user` pour peupler `profiles`
      // + `user_consents` (traçabilité RGPD art. 7).
      const data: Record<string, string> = {};
      if (options?.fullName) data.full_name = options.fullName;
      if (options?.termsVersion) data.terms_version = options.termsVersion;
      if (options?.termsAcceptedAt)
        data.terms_accepted_at = options.termsAcceptedAt;
      // Attribution boucle virale : variante du CTA vue par le répondant avant
      // inscription (cookieless, cf. spec 2026-06-22-cta-viral-tracking). Validée
      // côté appelant ∈ {A,B,C} ; absente => aucun champ (pas de défaut parasite).
      if (options?.ctaVariant) data.cta_variant = options.ctaVariant;

      // Attribution marketing (intérêt légitime RGPD art. 6.1.f) : champs
      // capturés au 1er chargement (cf. lib/utmTracking). Clés snake_case lues
      // directement par le trigger SQL `handle_new_user` → `profiles`.
      const attribution = getAttribution();
      if (attribution) {
        if (attribution.utm_source) data.utm_source = attribution.utm_source;
        if (attribution.utm_medium) data.utm_medium = attribution.utm_medium;
        if (attribution.utm_campaign)
          data.utm_campaign = attribution.utm_campaign;
        if (attribution.utm_content) data.utm_content = attribution.utm_content;
        if (attribution.landing_path)
          data.landing_path = attribution.landing_path;
        if (attribution.referrer) data.referrer = attribution.referrer;
      }

      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: Object.keys(data).length > 0 ? { data } : undefined,
      });
      if (error) throw error;
      // Inscription réussie : purge l'attribution pour ne pas re-taguer une
      // seconde inscription sur le même appareil (poste partagé).
      clearAttribution();
      return { success: true };
    } catch (err: any) {
      setError(err.message);
      return { error: err.message };
    }
  };

  const signInWithGoogle = async () => {
    try {
      // Récupérer la page de retour posée par AuthRequired ; à défaut, la page
      // courante. Normalisée (basename /date retiré) une seule fois, à l'écriture.
      const returnTo =
        peekAuthReturnTo() ?? stripKnownBasename(window.location.pathname);
      const redirectUrl = `${window.location.origin}/auth/callback?returnTo=${encodeURIComponent(returnTo)}`;

      // NOTE: Preview deployments require Supabase OAuth redirect URL configuration.
      // If preview login fails with "Redirect URL mismatch", add this URL to:
      // Supabase Dashboard > Settings > Authentication > URL Configuration > Redirect URLs
      // Example preview URL: https://doo-dates-form-polls-git-staging-doodates.vercel.app/auth/callback
      console.log("Google OAuth redirect URL:", redirectUrl);

      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: redirectUrl,
        },
      });
      if (error) throw error;
      return { success: true };
    } catch (err: any) {
      setError(err.message);
      return { error: err.message };
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) return { error: "Not authenticated" };

    try {
      const { error } = await supabase
        .from("profiles")
        .update(updates)
        .eq("id", user.id);

      if (error) throw error;

      // Le nom vit à deux endroits : profiles.full_name (affichage aux votants)
      // et user_metadata.full_name (menu, carte de profil). Une rectification
      // (RGPD art. 16) doit mettre à jour les deux. Échec non bloquant : la
      // source persistée, profiles, est déjà à jour.
      if ("full_name" in updates) {
        const { error: metaError } = await supabase.auth.updateUser({
          data: { full_name: updates.full_name ?? null },
        });
        if (metaError) {
          logger.warn("user_metadata.full_name non synchronisé", "auth");
        }
      }

      await refreshProfile();
      return { success: true };
    } catch (err: any) {
      setError(err.message);
      return { error: err.message };
    }
  };

  // UN SEUL EFFET - Simple et fiable
  useEffect(() => {
    setLoading(true);

    // Bug #1 — safety net: si onAuthStateChange ne fire jamais INITIAL_SESSION
    // (réseau capricieux, Supabase indisponible), on débloque l'UI au bout
    // de 8s pour éviter un spinner infini sur /tableau-de-bord.
    const safetyTimeout = setTimeout(() => {
      logger.warn("Auth init timeout — proceeding as unauthenticated", "auth");
      setLoading(false);
    }, 8000);

    // SIGNED_IN est ré-émis au retour de focus : ne migrer qu'une fois par
    // utilisateur (claim_guest_polls est plafonnée à 10/h, #1277).
    const guestMigration = createOncePerSignIn();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      clearTimeout(safetyTimeout);
      console.log(`🔥 Auth event: ${event}`, session?.user?.id);

      if (event === "SIGNED_OUT") {
        guestMigration.reset();
      }

      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        const profileData = await fetchProfile(session.user.id);
        setProfile(profileData);
      } else {
        setProfile(null);
      }

      // Important : débloquer l'UI AVANT la migration guest→user. La migration
      // est fire-and-forget — si elle traîne (réseau lent, RLS), elle ne doit
      // jamais bloquer le rendu (sinon spinner infini sur /admin, /dashboard…).
      setLoading(false);
      setError(null);

      if (
        session?.user &&
        event === "SIGNED_IN" &&
        guestMigration.shouldRun(session.user.id)
      ) {
        (async () => {
          try {
            const { getFingerprint } =
              await import("../lib/guestIdentityService");
            const guestFingerprint = await getFingerprint();

            const [{ pollService }, { migrateGuestConversationsToUser }] =
              await Promise.all([
                import("../services/PollService"),
                import("../lib/storage/ConversationStorageSupabase"),
              ]);

            // Sondage reçu par lien d'admin : rattaché SEULEMENT si l'invité
            // l'a demandé (« Créer un compte » dans la page d'admin), jamais
            // par la seule connexion (28/09 : un co-organisateur connecté
            // s'appropriait le sondage transféré). Ne lève jamais.
            const pendingClaim = import("../lib/pendingPollClaim").then(
              ({ processPendingPollClaim }) =>
                processPendingPollClaim(guestFingerprint),
            );

            const [migratedPolls, migratedConvs] = await Promise.all([
              pollService.migrateGuestPollsToUser(
                session.user.id,
                guestFingerprint,
              ),
              migrateGuestConversationsToUser(
                session.user.id,
                guestFingerprint,
              ),
              pendingClaim,
            ]);

            // Doctrine 2026-09-10 : anonyme et Free partagent UN SEUL quota
            // de 3 conversations IA à vie — se créer un compte ne doit
            // jamais redonner 3 essais frais. On reporte l'usage anonyme
            // déjà consommé (guest_quotas) vers le compteur authentifié
            // (user_ai_credits) AVANT tout premier appel à consume_ai_credit,
            // pour que celui-ci le trouve déjà "chargé" du passé anonyme.
            const { error: mergeQuotaError } = await supabase.rpc(
              "merge_guest_ai_quota_to_user",
              {
                p_user_id: session.user.id,
                p_fingerprint: guestFingerprint,
              },
            );
            if (mergeQuotaError) {
              logger.warn(
                "Failed to merge guest AI quota into user account",
                "auth",
                mergeQuotaError,
              );
            }

            if (migratedPolls > 0 || migratedConvs > 0) {
              logger.info("Guest data migrated to authenticated user", "auth", {
                userId: session.user.id,
                migratedPolls,
                migratedConvs,
              });
            }
          } catch (migrationError) {
            logger.warn(
              "Failed to migrate guest data at sign-in",
              "auth",
              migrationError,
            );
          }
        })();
      }
    });

    return () => {
      clearTimeout(safetyTimeout);
      subscription.unsubscribe();
    };
  }, []);

  const value: AuthContextType = {
    user,
    session,
    profile,
    loading,
    error,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    updateProfile,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
